/**
 * poBuild · «Конструктор Page Object» — собери класс из россыпи строк (S15 · T05).
 * Per ADR-014. Tap строки → tap зоны (init / method / trash); строка уезжает в зону,
 * tap строки в зоне — возврат в пул. «Собрать» валидирует место (data-zone) + порядок
 * в методе (data-order); на успехе строки складываются в скелет класса (payoff-сборка).
 *
 * Контракт разметки:
 *   <div class="po-build" data-fin="мораль">
 *     <div class="pob-head"><span class="pob-ttl">…</span>
 *       <span class="pob-cnt">размещено <b class="pob-num">0</b>/N</span></div>
 *     <div class="pob-instr">…</div>
 *     <pre class="pob-frame"><code>class LoginPage:
 *     def __init__(self, page):
 *   <span class="pob-drop" data-zone="init"></span>
 *     def login(self, user, pwd):
 *   <span class="pob-drop" data-zone="method"></span></code></pre>
 *     <div class="pob-zones"><button type="button" class="pob-zone" data-zone="init">…</button>
 *       <button … data-zone="method">…</button><button … data-zone="trash">…</button></div>
 *     <div class="pob-pool">
 *       <div class="pob-line" data-zone="init">self.username = …<span class="pob-why">…</span></div>
 *       <div class="pob-line" data-zone="method" data-order="2">self.username.fill(user)<span class="pob-why">…</span></div>
 *       <div class="pob-line" data-zone="trash">page.goto(…)<span class="pob-why">шаг теста, не POM</span></div>
 *     </div>
 *     <button type="button" class="pob-check">Собрать</button>
 *     <div class="pob-verdict" aria-live="polite"></div>
 *   </div>
 *
 * No-JS: строки и подсказки видны под скелетом. StrictMode-safe (guard + cleanup
 * восстанавливает innerHTML). См. feedback_qa_studio_strictmode_interactive_modules.
 */
export function initPoBuild(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>('.po-build').forEach((el) => {
    if (el.dataset.pobInit === '1') return;
    el.dataset.pobInit = '1';
    const original = el.innerHTML;

    const pool = el.querySelector<HTMLElement>('.pob-pool');
    const zoneBtns = Array.from(el.querySelectorAll<HTMLButtonElement>('.pob-zone'));
    const checkBtn = el.querySelector<HTMLButtonElement>('.pob-check');
    const numEl = el.querySelector<HTMLElement>('.pob-num');
    const verdict = el.querySelector<HTMLElement>('.pob-verdict');
    const lines = Array.from(el.querySelectorAll<HTMLElement>('.pob-line'));
    if (!pool || !checkBtn || zoneBtns.length === 0 || lines.length === 0) return;

    el.classList.add('pob-ready');
    const total = lines.length;
    let selected: HTMLElement | null = null;

    const codeOf = (l: HTMLElement): string =>
      l.firstChild && l.firstChild.nodeType === 3
        ? (l.firstChild.textContent ?? '').trim()
        : (l.textContent ?? '').trim();

    // Бакет под каждой зоной (создаём в JS, чтобы не требовать его в разметке).
    const buckets = new Map<string, HTMLElement>();
    zoneBtns.forEach((btn) => {
      const zone = btn.dataset.zone ?? '';
      const bucket = document.createElement('div');
      bucket.className = 'pob-bucket';
      bucket.dataset.zone = zone;
      btn.insertAdjacentElement('afterend', bucket);
      buckets.set(zone, bucket);
    });

    const updateCount = () => {
      const assigned = lines.filter((l) => l.dataset.cur).length;
      if (numEl) numEl.textContent = String(assigned);
      checkBtn.disabled = assigned !== total;
    };

    const select = (line: HTMLElement | null) => {
      if (selected) selected.classList.remove('pob-sel');
      selected = line;
      if (selected) selected.classList.add('pob-sel');
    };

    const clearMarks = () => {
      lines.forEach((l) => l.classList.remove('pob-wrong'));
      el.querySelectorAll<HTMLElement>('.pob-why.pob-show').forEach((w) => w.classList.remove('pob-show'));
      if (verdict) {
        verdict.textContent = '';
        verdict.className = 'pob-verdict';
      }
    };

    const onLineClick = (e: Event) => {
      const line = e.currentTarget as HTMLElement;
      if (el.classList.contains('pob-done')) return;
      clearMarks();
      if (line.dataset.cur) {
        delete line.dataset.cur; // вернуть в пул
        pool.appendChild(line);
        select(null);
        updateCount();
      } else {
        select(selected === line ? null : line);
      }
    };

    const onZoneClick = (e: Event) => {
      const btn = e.currentTarget as HTMLElement;
      const zone = btn.dataset.zone ?? '';
      if (!selected || el.classList.contains('pob-done')) return;
      clearMarks();
      selected.dataset.cur = zone;
      buckets.get(zone)?.appendChild(selected);
      select(null);
      updateCount();
    };

    const assemble = () => {
      const fill = (zone: string, ordered: boolean) => {
        const drop = el.querySelector<HTMLElement>(`.pob-drop[data-zone="${zone}"]`);
        if (!drop) return;
        const zl = lines.filter((l) => l.dataset.zone === zone);
        if (ordered) {
          zl.sort((a, b) => Number(a.dataset.order ?? 0) - Number(b.dataset.order ?? 0));
        }
        drop.textContent = zl.map((l) => '        ' + codeOf(l)).join('\n');
      };
      fill('init', false);
      fill('method', true);
      el.classList.add('pob-done');
      if (verdict) {
        verdict.textContent = el.dataset.fin || 'Собрано верно — вот твой Page Object.';
        verdict.classList.add('pob-okmsg');
      }
    };

    const onCheck = () => {
      if (el.classList.contains('pob-done')) return;
      clearMarks();
      let wrong = 0;
      lines.forEach((l) => {
        if ((l.dataset.cur ?? '') !== (l.dataset.zone ?? '')) {
          l.classList.add('pob-wrong');
          l.querySelector<HTMLElement>('.pob-why')?.classList.add('pob-show');
          wrong += 1;
        }
      });
      // порядок внутри метода
      let orderBad = false;
      const inMethod = Array.from(buckets.get('method')?.querySelectorAll<HTMLElement>('.pob-line') ?? [])
        .filter((l) => l.dataset.zone === 'method')
        .map((l) => Number(l.dataset.order ?? 0));
      for (let i = 1; i < inMethod.length; i += 1) {
        if (inMethod[i]! < inMethod[i - 1]!) orderBad = true;
      }
      if (wrong > 0 || orderBad) {
        if (verdict) {
          verdict.textContent =
            wrong > 0
              ? `Не на месте: ${wrong}. Открой подсказку у подсвеченных и перенеси.`
              : 'Размещение верное, но порядок действий в методе перепутан — переставь.';
          verdict.classList.add('pob-errmsg');
        }
        return;
      }
      assemble();
    };

    lines.forEach((l) => {
      l.setAttribute('role', 'button');
      l.tabIndex = 0;
      l.addEventListener('click', onLineClick);
    });
    zoneBtns.forEach((b) => b.addEventListener('click', onZoneClick));
    checkBtn.addEventListener('click', onCheck);
    updateCount();

    cleanups.push(() => {
      lines.forEach((l) => l.removeEventListener('click', onLineClick));
      zoneBtns.forEach((b) => b.removeEventListener('click', onZoneClick));
      checkBtn.removeEventListener('click', onCheck);
      el.innerHTML = original;
      el.classList.remove('pob-ready', 'pob-done');
      delete el.dataset.pobInit;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
